import { useEffect, useRef, useState } from "react";
import { Loader2, Pencil, Plus, Sparkles, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, img } from "@/lib/api";

const EMPTY = { title: "", excerpt: "", content: "", cover: "", category: "Cuidados", products: [], published: true };

const AiDraft = ({ slugs, onDraft }) => {
  const [topic, setTopic] = useState("");
  const [prods, setProds] = useState("");
  const [busy, setBusy] = useState(false);
  const gen = async () => {
    if (topic.trim().length < 3) return toast.error("Escribe un tema para el artículo");
    const products = prods.split(",").map((s) => s.trim()).filter(Boolean);
    const bad = products.filter((s) => !slugs.includes(s));
    if (bad.length) return toast.error(`Productos desconocidos: ${bad.join(", ")}`);
    setBusy(true);
    const [draft, cover] = await Promise.allSettled([
      api.post("/admin/blog/generate", { topic, products }),
      api.post("/admin/blog/cover", { topic }),
    ]);
    if (draft.status === "fulfilled") {
      onDraft({ ...draft.value.data, cover: cover.status === "fulfilled" ? cover.value.data.url : undefined });
      toast.success(cover.status === "fulfilled" ? "Borrador y portada generados. Revísalos antes de publicar." : "Borrador generado (la portada falló, puedes reintentarla).");
    } else toast.error(errMsg(draft.reason));
    setBusy(false);
  };
  return (
    <div className="bg-[#EFF6FF] rounded p-4 space-y-2" data-testid="ai-draft-box">
      <p className="font-display font-bold text-lg text-[#0B1D3A] flex items-center gap-2"><Sparkles size={18} className="text-[#DC2626]" /> Generar borrador con IA</p>
      <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Tema: p. ej. Cómo preparar al caballo para su primer concurso" data-testid="ai-topic-input" />
      <div className="flex gap-2">
        <Input value={prods} onChange={(e) => setProds(e.target.value)} placeholder="Productos FAUNNY (opcional): calma-t, isotonic" data-testid="ai-products-input" />
        <button type="button" onClick={gen} disabled={busy} data-testid="ai-generate-button"
          className="shrink-0 bg-[#0B1D3A] text-white text-sm font-bold px-4 rounded-full flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <><Loader2 size={15} className="animate-spin" /> Escribiendo…</> : "Generar"}
        </button>
      </div>
      <p className="text-xs text-slate-500">La IA rellena título, entradilla, categoría, contenido y productos. Se guarda como borrador para que lo revises.</p>
    </div>
  );
};

const PostEditor = ({ post, slugs, onClose, onSaved }) => {
  const [p, setP] = useState({ ...EMPTY, ...post, productsText: (post?.products || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const ref = useRef(null);
  const genCover = async () => {
    const topic = (p.title || p.excerpt).trim();
    if (topic.length < 3) return toast.error("Escribe primero el título del artículo");
    setCoverBusy(true);
    try { const { data } = await api.post("/admin/blog/cover", { topic }); setP((x) => ({ ...x, cover: data.url })); toast.success("Portada generada"); }
    catch (e2) { toast.error(errMsg(e2)); }
    setCoverBusy(false);
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd); setP((x) => ({ ...x, cover: data.url })); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const save = async (e) => {
    e.preventDefault();
    const products = p.productsText.split(",").map((s) => s.trim()).filter(Boolean);
    const bad = products.filter((s) => !slugs.includes(s));
    if (bad.length) return toast.error(`Productos desconocidos: ${bad.join(", ")}`);
    setBusy(true);
    const body = { title: p.title, excerpt: p.excerpt, content: p.content, cover: p.cover, category: p.category, products, published: p.published };
    try {
      if (post?.id) await api.put(`/admin/blog/${post.id}`, body); else await api.post("/admin/blog", body);
      toast.success("Artículo guardado");
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="post-editor">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B1D3A]">{post?.id ? "Editar artículo" : "Nuevo artículo"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <AiDraft slugs={slugs} onDraft={(d) => setP((x) => ({ ...x, title: d.title, excerpt: d.excerpt, category: d.category, content: d.content, productsText: d.products.join(", "), cover: d.cover || x.cover, published: false }))} />
          <div><Label>Título</Label><Input required value={p.title} onChange={set("title")} data-testid="post-title-input" className="mt-1" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Categoría</Label><Input value={p.category} onChange={set("category")} data-testid="post-category-input" className="mt-1" /></div>
            <div><Label>Productos relacionados (slugs)</Label><Input value={p.productsText} onChange={set("productsText")} placeholder="sand-free, isotonic" data-testid="post-products-input" className="mt-1" /></div>
          </div>
          <div><Label>Imagen de portada</Label>
            <div className="flex gap-3 items-center mt-1">
              {p.cover && <img src={img(p.cover)} alt="" className="w-24 h-16 object-cover rounded" />}
              <Input value={p.cover} onChange={set("cover")} placeholder="URL de la imagen" data-testid="post-cover-input" />
              <button type="button" onClick={() => ref.current?.click()} className="shrink-0 border rounded px-3 py-2 text-sm flex items-center gap-1" data-testid="post-cover-upload"><Upload size={15} /> Subir</button>
              <button type="button" onClick={genCover} disabled={coverBusy} className="shrink-0 bg-[#0B1D3A] text-white rounded px-3 py-2 text-sm flex items-center gap-1 disabled:opacity-60" data-testid="post-cover-ai">
                {coverBusy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} IA
              </button>
              <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} />
            </div>
          </div>
          <div><Label>Entradilla</Label><Textarea rows={2} value={p.excerpt} onChange={set("excerpt")} data-testid="post-excerpt-input" className="mt-1" /></div>
          <div><Label>Contenido</Label>
            <p className="text-xs text-slate-500">Separa los párrafos con una línea en blanco. "## " al inicio de una línea crea un subtítulo y "- " crea un punto de lista. **texto** pone negrita.</p>
            <Textarea rows={14} value={p.content} onChange={set("content")} data-testid="post-content-input" className="mt-1 font-mono text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm"><Switch checked={p.published} onCheckedChange={(v) => setP({ ...p, published: v })} data-testid="post-published-switch" /> Publicado</label>
          <button disabled={busy} data-testid="post-save-button" className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-display font-bold text-lg py-3 rounded-full">{busy ? "Guardando…" : "Guardar artículo"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AdminBlog = ({ slugs }) => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);
  const del = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.title}"?`)) return;
    await api.delete(`/admin/blog/${p.id}`);
    toast.success("Artículo eliminado");
    load();
  };
  return (
    <div data-testid="admin-blog">
      <button onClick={() => setEditing(null)} data-testid="admin-new-post" className="mb-4 flex items-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-display font-bold px-5 py-2.5 rounded-full"><Plus size={17} /> Nuevo artículo</button>
      <div className="space-y-2">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center gap-4 border border-slate-200 rounded p-3" data-testid={`admin-post-${p.slug}`}>
            <img src={img(p.cover)} alt="" className="w-20 h-14 object-cover rounded bg-slate-100" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{p.title}</p>
              <p className="text-xs text-slate-500">{p.category} · {new Date(p.created_at).toLocaleDateString("es-ES")} · {p.published ? "Publicado" : "Borrador"}{p.auto ? " · Automático (IA)" : ""}{!p.published && p.auto_publish_at ? <span className="text-red-600 font-semibold" data-testid={`post-autopublish-${p.slug}`}> · Se publicará solo el {new Date(p.auto_publish_at).toLocaleDateString("es-ES")}</span> : null}</p>
            </div>
            <button onClick={() => setEditing(p)} className="p-2 hover:text-[#DC2626]" data-testid={`admin-edit-post-${p.slug}`} aria-label="Editar"><Pencil size={16} /></button>
            <button onClick={() => del(p)} className="p-2 hover:text-[#DC2626]" data-testid={`admin-delete-post-${p.slug}`} aria-label="Eliminar"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing !== undefined && <PostEditor key={editing?.id || "new"} post={editing} slugs={slugs} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
};
